import type {
  AssignmentSubmission,
  Correction,
  Deposit,
  MainCorrection,
} from '@didaskill/domain/assignment-submissions/types';
import {
  type ASSIGNMENT_ASSESSMENTS,
  type ASSIGNMENT_COMMENT_REASON,
  type ASSIGNMENT_SUBMISSION_ASSESSMENT_LEVELS,
  submissionAssessmentLabels,
} from '@didaskill/domain/assignments/constants';
import type { MessageDescriptor } from '@didaskill/types/misc';

import type { AssignmentAiContextFormatMessage } from './assignmentAiContext.formatMessage';
import { assignmentAiContextMessages } from './assignmentAiContext.messages';

/**
 * Extracts correction details from a submission.
 *
 * @param depositCorrection Correction object from a deposit.
 * @param mainCorrection Main correction object from a submission.
 * @returns Object containing existing details like assessment, grade, comment etc.
 */
export const getSubmissionCorrectionDetails = ({
  depositCorrection,
  mainCorrection,
}: {
  depositCorrection?: Correction;
  mainCorrection?: MainCorrection;
}): {
  assessment?: ASSIGNMENT_ASSESSMENTS;
  comment?: string;
  feedback?: string;
  globalFeedback?: string;
  grade?: number;
  reason?: ASSIGNMENT_COMMENT_REASON;
} => {
  if (!depositCorrection && !mainCorrection) {
    return {};
  }

  const depositGrade =
    depositCorrection && 'grade' in depositCorrection && Number.isFinite(depositCorrection.grade)
      ? depositCorrection.grade
      : undefined;

  const depositAssessment =
    (depositCorrection && 'assessment' in depositCorrection && depositCorrection.assessment) ||
    undefined;

  const mainGrade =
    mainCorrection && 'grade' in mainCorrection && Number.isFinite(mainCorrection.grade)
      ? mainCorrection.grade
      : undefined;

  const mainAssessment =
    (mainCorrection && 'assessment' in mainCorrection && mainCorrection.assessment) || undefined;

  const feedback =
    depositCorrection && 'feedback' in depositCorrection ? depositCorrection.feedback : undefined;

  const comment =
    depositCorrection && 'comment' in depositCorrection ? depositCorrection.comment : undefined;

  const reason =
    depositCorrection && 'reason' in depositCorrection ? depositCorrection.reason : undefined;

  const globalFeedback =
    mainCorrection && 'feedback' in mainCorrection ? mainCorrection.feedback : undefined;

  return {
    assessment: depositAssessment || mainAssessment,
    comment,
    feedback,
    globalFeedback,
    grade: depositGrade ?? mainGrade,
    reason,
  };
};

/**
 * Get assessment message based on average score.
 *
 * @param average - Average score of the student.
 * @returns Message descriptor corresponding to the assessment level.
 */
export function getStudentTranscriptAssessmentLabel(
  average: number,
  messages: Record<ASSIGNMENT_SUBMISSION_ASSESSMENT_LEVELS, MessageDescriptor>,
): MessageDescriptor {
  switch (true) {
    default:
    case average < 8:
      return messages.ASSESSMENT_LOWEST;
    case average < 10:
      return messages.ASSESSMENT_LOW;
    case average < 12:
      return messages.ASSESSMENT_MEDIUM;
    case average < 14:
      return messages.ASSESSMENT_MEDIUM_HIGH;
    case average < 16:
      return messages.ASSESSMENT_HIGH;
    case average >= 16:
      return messages.ASSESSMENT_HIGHEST;
  }
}

/**
 * Formats an assignment assessment label for AI context prompts.
 * @param assessment Assessment enum value from the correction, if any.
 * @param formatMessage Localized message formatter (e.g. `intl.formatMessage`).
 * @returns Localized assessment label or a fallback placeholder.
 */
function formatAssessmentLabel(
  assessment: ASSIGNMENT_ASSESSMENTS | undefined,
  formatMessage: AssignmentAiContextFormatMessage,
): string {
  if (!assessment) {
    return formatMessage(assignmentAiContextMessages.ASSESSMENT_NOT_PROVIDED);
  }

  const assessmentDescriptor = submissionAssessmentLabels[assessment];

  if (assessmentDescriptor) {
    return formatMessage(assessmentDescriptor);
  }

  return assessment;
}

/**
 * Builds the plain-text context block sent to M.ia for pre-submission review.
 * @param params Assignment and deposit metadata assembled for the AI prompt.
 * @param params.formatMessage Localized message formatter (e.g. `intl.formatMessage`).
 * @param params.assignmentTitle Assignment title shown to the model.
 * @param params.assignmentDescription Free-text assignment description from the platform.
 * @param params.gradingScale Maximum grade scale when configured.
 * @param params.deposit Current in-progress student deposit.
 * @param params.instructionDocumentNames Instructor document file names attached to the assignment.
 * @param params.loadedInstructionDocumentNames Instructor documents successfully analyzed for the prompt.
 * @param params.loadedStudentDocumentNames Student documents successfully analyzed for the prompt.
 * @param params.skippedDocumentNames Files that could not be analyzed, with optional reasons.
 * @returns Newline-separated localized context block for the generative model.
 */
export function buildAssignmentPreSubmissionAiContextText({
  formatMessage,
  assignmentTitle,
  assignmentDescription,
  gradingScale,
  deposit,
  instructionDocumentNames,
  loadedInstructionDocumentNames,
  loadedStudentDocumentNames,
  skippedDocumentNames,
}: {
  formatMessage: AssignmentAiContextFormatMessage;
  assignmentTitle: string;
  assignmentDescription?: string | null;
  gradingScale?: number | null;
  deposit: Deposit;
  instructionDocumentNames: string[];
  loadedInstructionDocumentNames: string[];
  loadedStudentDocumentNames: string[];
  skippedDocumentNames: string[];
}): string {
  const trimmedDescription = assignmentDescription?.trim();
  const studentComment =
    deposit.studentComment?.trim() ||
    formatMessage(assignmentAiContextMessages.STUDENT_COMMENT_NONE);
  const notAnalyzedAttachmentList =
    deposit.attachments
      ?.map((file) => file.fileName)
      .filter(Boolean)
      .join(', ') || formatMessage(assignmentAiContextMessages.NO_FILE);

  const lines = [
    formatMessage(assignmentAiContextMessages.TITLE, { assignmentTitle }),
    gradingScale
      ? formatMessage(assignmentAiContextMessages.GRADING_SCALE, { gradingScale })
      : null,
    formatMessage(assignmentAiContextMessages.PRE_SUBMISSION_PROCESS),
    trimmedDescription
      ? formatMessage(assignmentAiContextMessages.DESCRIPTION_SUPPLEMENT, {
          description: trimmedDescription,
        })
      : null,
    instructionDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.INSTRUCTOR_MODEL, {
          documentNames: instructionDocumentNames.join(', '),
        })
      : null,
    loadedInstructionDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.ANALYZED_INSTRUCTION, {
          documentNames: loadedInstructionDocumentNames.join(', '),
        })
      : null,
    !loadedInstructionDocumentNames.length && instructionDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.INSTRUCTION_NOT_ANALYZED)
      : null,
    formatMessage(assignmentAiContextMessages.STUDENT_DEPOSIT_COMMENT, { comment: studentComment }),
    loadedStudentDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.STUDENT_SUBMISSION_ANALYZED, {
          documentNames: loadedStudentDocumentNames.join(', '),
        })
      : formatMessage(assignmentAiContextMessages.STUDENT_SUBMISSION_NOT_ANALYZED, {
          documentNames: notAnalyzedAttachmentList,
        }),
    skippedDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.SKIPPED_FILES, {
          documentNames: skippedDocumentNames.join(', '),
        })
      : null,
    loadedStudentDocumentNames.length
      ? formatMessage(assignmentAiContextMessages.PRE_SUBMISSION_ANALYSIS_INSTRUCTION)
      : null,
  ];

  return lines.filter(Boolean).join('\n');
}

/**
 * Builds a plain-text context block for assignment-related AI prompts.
 * @param params.formatMessage Localized message formatter (e.g. `intl.formatMessage`).
 * @param params.assignmentTitle The title of the assignment.
 * @param params.assignmentDescription The description of the assignment.
 * @param params.gradingScale The grading scale of the assignment.
 * @param params.deposit The deposit of the assignment.
 * @param params.submission The submission of the assignment.
 * @returns The localized plain-text context block for assignment-related AI prompts.
 */
export function buildAssignmentAiContextText({
  formatMessage,
  assignmentTitle,
  assignmentDescription,
  gradingScale,
  deposit,
  submission,
}: {
  formatMessage: AssignmentAiContextFormatMessage;
  assignmentTitle: string;
  assignmentDescription: string;
  gradingScale?: number | null;
  deposit: Deposit;
  submission: AssignmentSubmission;
}): string {
  const { feedback, comment, globalFeedback, grade, assessment } = getSubmissionCorrectionDetails({
    depositCorrection: deposit.correction,
    mainCorrection: submission.correction,
  });

  const attachmentList =
    deposit.attachments
      ?.map((file) => file.fileName)
      .filter(Boolean)
      .join(', ') || formatMessage(assignmentAiContextMessages.NO_FILE);
  const studentComment =
    deposit.studentComment?.trim() ||
    formatMessage(assignmentAiContextMessages.STUDENT_COMMENT_NONE);
  const description =
    assignmentDescription.trim() ||
    formatMessage(assignmentAiContextMessages.DESCRIPTION_NOT_PROVIDED);

  const lines = [
    formatMessage(assignmentAiContextMessages.TITLE, { assignmentTitle }),
    formatMessage(assignmentAiContextMessages.DESCRIPTION, { assignmentDescription: description }),
    gradingScale
      ? formatMessage(assignmentAiContextMessages.GRADING_SCALE, { gradingScale })
      : null,
    formatMessage(assignmentAiContextMessages.STUDENT_COMMENT, { comment: studentComment }),
    formatMessage(assignmentAiContextMessages.ATTACHMENTS, { attachmentList }),
  ];

  if (feedback || comment || globalFeedback || grade != null || assessment) {
    let gradeLine: string | null = null;

    if (grade != null) {
      gradeLine = gradingScale
        ? formatMessage(assignmentAiContextMessages.GRADE_WITH_SCALE, { grade, gradingScale })
        : formatMessage(assignmentAiContextMessages.GRADE_WITHOUT_SCALE, { grade });
    }

    lines.push(
      formatMessage(assignmentAiContextMessages.CORRECTION_SECTION),
      globalFeedback
        ? formatMessage(assignmentAiContextMessages.GLOBAL_FEEDBACK, { globalFeedback })
        : null,
      feedback ? formatMessage(assignmentAiContextMessages.DEPOSIT_FEEDBACK, { feedback }) : null,
      comment ? formatMessage(assignmentAiContextMessages.TEACHER_COMMENT, { comment }) : null,
      gradeLine,
      formatMessage(assignmentAiContextMessages.ASSESSMENT, {
        assessment: formatAssessmentLabel(assessment, formatMessage),
      }),
    );
  }

  return lines.filter(Boolean).join('\n');
}
